import type {MouseEvent, ReactElement} from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import {isMobile} from 'react-device-detect';
import {ClickableDiv} from '@Components/clickable-div';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {Button, ButtonType, ButtonSize} from '@Components/button';
import {convertSecondsToMMSSFormat} from '@Utils/date.util';
import {noop} from '@Utils/general.util';
import type {FullscreenVideoPreviewProps} from './fullscreen-video-preview.types';
import {SLIDE_TRANSITION, SLIDE_VARIANTS} from './fullscreen-video-preview.types';
import styles from './fullscreen-video-preview.module.scss';

export function FullscreenVideoPreview<T extends {id: number}>(props: FullscreenVideoPreviewProps<T>): ReactElement | null {
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressTrackRef = useRef<HTMLDivElement>(null);
  const progressFillRef = useRef<HTMLDivElement>(null);
  const progressHandleRef = useRef<HTMLDivElement>(null);
  const rafIdRef = useRef<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [slideDirection, setSlideDirection] = useState(0);

  const updateProgressDom = (current: number, total: number): void => {
    if (!total) {
      return;
    }
    const percent = (current / total) * 100;
    if (progressFillRef.current) {
      progressFillRef.current.style.width = `${percent}%`;
    }
    if (progressHandleRef.current) {
      progressHandleRef.current.style.left = `${percent}%`;
    }
  };

  const tickProgress = (): void => {
    const videoEl = videoRef.current;
    if (videoEl?.duration) {
      updateProgressDom(videoEl.currentTime, videoEl.duration);
    }
    rafIdRef.current = requestAnimationFrame(tickProgress);
  };

  const startProgressTracking = (): void => {
    rafIdRef.current ??= requestAnimationFrame(tickProgress);
  };

  const stopProgressTracking = (): void => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  };

  useEffect(() => {
    return stopProgressTracking;
  }, []);

  useEffect(() => {
    const onContextMenuNative = (e: Event): void => {
      e.preventDefault();
    };
    document.addEventListener('contextmenu', onContextMenuNative, {capture: true});
    return (): void => {
      document.removeEventListener('contextmenu', onContextMenuNative, {capture: true});
    };
  }, []);

  const currentIndex = props.items.findIndex((item) => item.id === props.currentItemId);
  const currentItem = currentIndex >= 0 ? props.items[currentIndex] : null;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < props.items.length - 1;

  const navigate = useCallback(
    (direction: -1 | 1): void => {
      const newIndex = currentIndex + direction;
      if (newIndex < 0 || newIndex >= props.items.length) {
        return;
      }
      setSlideDirection(direction);
      props.onNavigate(props.items[newIndex]);
    },
    [currentIndex, props.items, props.onNavigate]
  );

  const togglePlay = useCallback((): void => {
    const videoEl = videoRef.current;
    if (!videoEl) {
      return;
    }
    if (videoEl.paused) {
      videoEl.play().catch(noop);
    } else {
      videoEl.pause();
    }
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        props.onClose();
      } else if (e.key === 'ArrowLeft' && hasPrev) {
        navigate(-1);
      } else if (e.key === 'ArrowRight' && hasNext) {
        navigate(1);
      } else if (e.key === ' ') {
        e.preventDefault();
        togglePlay();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return (): void => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [hasPrev, hasNext, navigate, togglePlay]);

  useEffect(() => {
    setCurrentTime(0);
    if (progressFillRef.current) {
      progressFillRef.current.style.width = '0%';
    }
    if (progressHandleRef.current) {
      progressHandleRef.current.style.left = '0%';
    }
    const videoEl = videoRef.current;
    if (videoEl) {
      videoEl.play().catch(noop);
    }
  }, [props.currentItemId]);

  const toggleMute = (): void => {
    const videoEl = videoRef.current;
    if (!videoEl) {
      return;
    }
    videoEl.muted = !videoEl.muted;
    setIsMuted(videoEl.muted);
  };

  const seekToClick = (e: MouseEvent<HTMLDivElement>): void => {
    const track = progressTrackRef.current;
    const videoEl = videoRef.current;
    if (!track || !videoEl || !duration) {
      return;
    }
    const rect = track.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    const newTime = Math.max(0, Math.min(duration, ratio * duration));
    videoEl.currentTime = newTime;
    setCurrentTime(newTime);
    updateProgressDom(newTime, duration);
  };

  if (!currentItem) {
    return null;
  }

  const previewData = props.getPreviewData(currentItem);
  const totalDuration = duration || previewData.duration;
  const isAudioDisabled = !previewData.hasAudio;

  const getPlayButton = (): ReactElement => {
    return (
      <ClickableDiv className={`flex-center _cursor-pointer ${styles.controlButton}`} onClick={togglePlay}>
        <Icon icon={isPlaying ? 'icon-pause' : 'icon-play-video'} size={IconSize.SIZE_ICON_20} />
      </ClickableDiv>
    );
  };

  const getTime = (): ReactElement => {
    return <Text className={styles.timeDisplay} size={TextSize.SM} val={`${convertSecondsToMMSSFormat(currentTime)} / ${convertSecondsToMMSSFormat(totalDuration)}`} />;
  };

  const getProgress = (): ReactElement => {
    return (
      <div ref={progressTrackRef} className={`_cursor-pointer _position-relative ${styles.progressTrack}`} onClick={seekToClick}>
        <div ref={progressFillRef} className={`_position-absolute _full-height ${styles.progressFill}`} />
        <div ref={progressHandleRef} className={`radius-round _position-absolute ${styles.progressHandle}`} />
      </div>
    );
  };

  const getSoundButton = (): ReactElement => {
    return (
      <ClickableDiv
        className={`flex-center _cursor-pointer radius-8 ${styles.controlButton} ${styles.soundIcon} ${isAudioDisabled ? styles.controlButtonDisabled : ''}`}
        onClick={toggleMute}
      >
        <Icon icon={isAudioDisabled || isMuted ? 'icon-sound-off' : 'icon-sound'} size={IconSize.SIZE_ICON_20} />
      </ClickableDiv>
    );
  };

  const getControlsBar = (): ReactElement => {
    if (isMobile) {
      return (
        <div className={`flex-v-row gap-3 _position-absolute ${styles.controlsBar} ${styles.controlsBarMobile}`}>
          {getProgress()}
          <div className="flexbox flex-align-items-center flex-justify-between gap-2">
            <div className="flexbox flex-align-items-center gap-2">
              {getPlayButton()}
              {getTime()}
            </div>
            {getSoundButton()}
          </div>
        </div>
      );
    }
    return (
      <div className={`flexbox flex-align-items-center gap-2 _position-absolute ${styles.controlsBar}`}>
        {getPlayButton()}
        {getTime()}
        {getProgress()}
        {getSoundButton()}
      </div>
    );
  };

  const getAddToDesignButton = (): ReactElement => {
    return (
      <div className={`_position-absolute ${styles.addToDesignButton}`}>
        <Button
          type={ButtonType.TERTIARY}
          size={isMobile ? ButtonSize.SMALL : ButtonSize.MEDIUM}
          text={window.i18next.t('pmwjs_add_to_design')}
          onClick={(): void => {
            props.onAddToDesign(currentItem);
          }}
        />
      </div>
    );
  };

  return (
    <ClickableDiv
      className={`flex-center _full-width _full-height _position-fixed ${styles.backdrop}`}
      onClick={(): void => {
        props.onClose();
      }}
    >
      <div
        className="flex-v-row flex-center _position-relative gap-6 _full-width _full-height"
        onClick={(e): void => {
          e.stopPropagation();
        }}
      >
        <div className={`flex-center gap-8 ${styles.videoContainer} ${isMobile ? styles.videoContainerMobile : ''}`}>
          <ClickableDiv
            className={`flex-center radius-round spacing-p-3 _cursor-pointer ${styles.navArrow} ${!hasPrev ? styles.navArrowDisabled : ''}`}
            onClick={(e): void => {
              e.stopPropagation();
              if (hasPrev) {
                navigate(-1);
              }
            }}
          >
            <Icon icon="icon-caret-left" size={IconSize.SIZE_ICON_24} />
          </ClickableDiv>
          <AnimatePresence custom={slideDirection} initial={false} mode="wait">
            <motion.div
              key={currentItem.id}
              custom={slideDirection}
              variants={SLIDE_VARIANTS}
              initial="enter"
              animate="center"
              exit="exit"
              transition={SLIDE_TRANSITION}
              className="_full-width _full-height flex-center"
            >
              <video
                ref={videoRef}
                className={`radius-12 _cursor-pointer ${previewData.width > previewData.height || isMobile ? '_full-height' : '_full-width'} ${styles.video}`}
                style={{aspectRatio: `${previewData.width} / ${previewData.height}`}}
                src={previewData.url}
                poster={previewData.thumbUrl}
                autoPlay
                playsInline
                muted={isMuted}
                width={previewData.width}
                height={previewData.height}
                onClick={togglePlay}
                onContextMenu={(e): void => {
                  e.preventDefault();
                }}
                onTimeUpdate={(): void => {
                  if (videoRef.current) {
                    setCurrentTime(videoRef.current.currentTime);
                  }
                }}
                onLoadedMetadata={(): void => {
                  if (videoRef.current) {
                    setDuration(videoRef.current.duration);
                  }
                }}
                onEnded={(): void => {
                  setIsPlaying(false);
                  stopProgressTracking();
                  updateProgressDom(1, 1);
                }}
                onPlay={(): void => {
                  setIsPlaying(true);
                  startProgressTracking();
                }}
                onPause={(): void => {
                  setIsPlaying(false);
                  stopProgressTracking();
                }}
              />
            </motion.div>
          </AnimatePresence>
          <ClickableDiv
            className={`flex-center radius-round spacing-p-3 _cursor-pointer ${styles.navArrow} ${!hasNext ? styles.navArrowDisabled : ''}`}
            onClick={(e): void => {
              e.stopPropagation();
              if (hasNext) {
                navigate(1);
              }
            }}
          >
            <Icon icon="icon-caret-right" size={IconSize.SIZE_ICON_24} />
          </ClickableDiv>
        </div>
        {getControlsBar()}
        <div className={`_position-absolute ${styles.closeButton}`}>
          <Icon
            icon="icon-arrow-left"
            size={IconSize.SIZE_ICON_20}
            shape={IconShape.SQUARE}
            type={IconType.DARK_GHOST}
            onClick={(): void => {
              props.onClose();
            }}
          />
        </div>
        {getAddToDesignButton()}
      </div>
    </ClickableDiv>
  );
}
